import React, { useMemo } from 'react'
import { DndContext } from '@dnd-kit/core'
import { SortableContext, arrayMove } from '@dnd-kit/sortable'

import { Show } from '@app/components/util'

import { isEmptyArray } from '@app/utils'
import {
  findPermittedAndSelectedDropdownMenuItems,
  findPermittedDropdownMenuItems,
  getSelectedDropdownMenuItems,
  getUnselectedDropdownMenuItems,
  getUpdatedDropdownMenuItemsByItem
} from '@app/components/DropdownMenu/utils'

import { MenuItem } from './components'
import { getFilteredItemsBySearchValue } from './utils'
import { INITIAL_CHECKBOX } from './constants'

import styles from './styles.module.scss'

import type { DragEndEvent } from '@dnd-kit/core'
import type { DropdownMenuItem } from '@app/types/dropdown'
import type { MenuProps } from './types'

export const Menu: React.FC<MenuProps> = ({ items = [], isSortable, isMultiple, searchValue, onSelect, onChange }) => {
  const permittedAndSelectedItems = useMemo<DropdownMenuItem[]>(
    () => findPermittedAndSelectedDropdownMenuItems(items),
    [items]
  )

  const permittedItems = useMemo<DropdownMenuItem[]>(() => findPermittedDropdownMenuItems(items), [items])

  const isAllCheck = useMemo<boolean>(
    () => !isEmptyArray(permittedItems) && permittedAndSelectedItems.length === permittedItems.length,
    [items]
  )

  const isAllDisable = useMemo<boolean>(() => isEmptyArray(permittedItems), [items])

  const isIndeterminate = useMemo<boolean>(
    () => !isEmptyArray(permittedAndSelectedItems) && permittedAndSelectedItems.length < permittedItems.length,
    [items]
  )

  const filteredItems = useMemo<DropdownMenuItem[]>(
    () => getFilteredItemsBySearchValue(items, searchValue),
    [items, searchValue]
  )

  const selectAllMenuItems = (): void => onSelect?.(getSelectedDropdownMenuItems(items))

  const unselectAllMenuItems = (): void => onSelect?.(getUnselectedDropdownMenuItems(items))

  const getUpdatedDropdownMenuByItem = (newItem: DropdownMenuItem): DropdownMenuItem[] => {
    const newDropdownMenuItems = isMultiple ? items.slice() : getUnselectedDropdownMenuItems(items)

    return getUpdatedDropdownMenuItemsByItem(newDropdownMenuItems, newItem)
  }

  const handleAllCheckboxClick = (newAllCheckboxMenuItem: DropdownMenuItem): void => {
    const isSelect = newAllCheckboxMenuItem.isSelect ?? false

    if (isSelect) {
      selectAllMenuItems()
    } else {
      unselectAllMenuItems()
    }
  }

  const handleDropdownMenuItemClick = (newItem: DropdownMenuItem): void => {
    const updatedItems = getUpdatedDropdownMenuByItem(newItem)

    onSelect?.(updatedItems, newItem)
  }

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const activeIndex = items.findIndex(({ id }) => id === active.id)
      const overIndex = items.findIndex(({ id }) => id === over.id)

      onChange?.(arrayMove(items, activeIndex, overIndex))
    }
  }

  return (
    <DndContext onDragEnd={ handleDragEnd }>
      <SortableContext items={ items }>
        <ul className={ styles.menu }>
          <Show condition={ isMultiple }>
            <MenuItem
              { ...INITIAL_CHECKBOX }
              withCheckbox
              isSelect={ isAllCheck }
              isDisable={ isAllDisable }
              checkbox={ { indeterminate: isIndeterminate } }
              onClick={ handleAllCheckboxClick }
            />
          </Show>

          { filteredItems.map(item => {
            return (
              <MenuItem
                { ...item }
                key={ item.id }
                isSortable={ isSortable }
                checkbox={ isMultiple }
                onClick={ handleDropdownMenuItemClick }
              />
            )
          }) }
        </ul>
      </SortableContext>
    </DndContext>
  )
}
